import { useMutation } from '@tanstack/react-query';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import {
  fetchProductsPricing,
  PricingType
} from '@setvi/shared/services/react-query';
import {
  extractItems,
  buildQuoteRequestBody,
  updateItemsWithPricing,
  mapQuoteItems,
  getItemId
} from '../utils/quote-helpers';
import { clearPricingFetchingState } from './use-refresh-single-price';
import { useQuoteCache } from './use-quote-cache';
import { useQueuedUpdateMutation } from './use-queued-update-mutation';

/**
 * Extract all products from quote items (including nested ones)
 * Uses a flat array accumulator pattern for efficiency
 */
const extractAllProducts = (
  items: QuoteItemDTO[]
): Array<{ sku: string; id: string }> => {
  const products: Array<{ sku: string; id: string }> = [];

  // Use mapQuoteItems to traverse, collecting products as a side effect
  mapQuoteItems(items, product => {
    if (!product.isFreeHand && product.sku) {
      products.push({
        sku: product.sku,
        id: getItemId(product)
      });
    }
    return product;
  });

  return products;
};

/**
 * Mark all non-freehand products as needing pricing update
 */
const markAllProductsForPricing = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
  mapQuoteItems(items, product => {
    if (!product.isFreeHand) {
      return { ...product, isPricingFetching: true };
    }
    return product;
  });

/**
 * Refresh all product prices in the quote
 * 1. Marks all products as fetching (optimistic update in onMutate)
 * 2. Fetches latest pricing from API
 * 3. Updates all products with new pricing data
 * 4. Saves complete quote
 */
export const useRefreshPrices = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const {
    mutateAsync: updateQuote,
    hasQueuedMutations,
    setOnQueueEmpty
  } = useQueuedUpdateMutation();

  return useMutation({
    mutationFn: async () => {
      await cache.cancel();

      // Get quote from cache (already marked for pricing by onMutate)
      const currentQuote = cache.getQuote();
      if (!currentQuote) {
        throw new Error('Quote not found in cache');
      }

      const allProducts = extractAllProducts(currentQuote.items);

      if (allProducts.length === 0) {
        return currentQuote;
      }

      // Create a copy to update with pricing
      const updatedQuote = { ...currentQuote };

      const accountNumber = currentQuote.account?.accountNumber;
      const segmentId = currentQuote.segment?._id;

      const pricingResponse = await fetchProductsPricing({
        accountNumber: accountNumber ?? segmentId,
        products: allProducts.map(product => ({
          sku: product.sku,
          quantity: 1
        })),
        type: PricingType.Quote
      });

      if (pricingResponse.pricing?.length > 0) {
        // Update items with new pricing (items already marked for pricing by onMutate)
        updatedQuote.items = updateItemsWithPricing(
          updatedQuote.items,
          pricingResponse.pricing
        );
      }

      // Clear loading state from all items
      updatedQuote.items = clearPricingFetchingState(updatedQuote.items);

      // Update cache immediately to clear loading state in UI
      cache.setQuote(() => updatedQuote);

      const items = extractItems(updatedQuote.items);

      return updateQuote({
        quoteId,
        body: buildQuoteRequestBody(currentQuote, items)
      });
    },

    onMutate: async () => {
      await cache.cancel();

      const previousQuote = cache.getQuote();

      // Mark all products as fetching for loading state
      cache.setQuote(old => {
        if (!old) return old;
        return {
          ...old,
          items: markAllProductsForPricing(old.items)
        };
      });

      return { previousQuote };
    },

    onError: (_error, _variables, context) => {
      // Rollback to previous state
      if (context?.previousQuote) {
        cache.setQuote(() => context.previousQuote);
      }
    },

    onSuccess: () => {
      // Skip refetch if there are queued mutations to preserve optimistic updates
      if (hasQueuedMutations()) {
        // Defer refetch until all queued mutations complete
        setOnQueueEmpty(() => cache.refetch());
      } else {
        // No queued mutations, refetch immediately
        cache.refetch();
      }
    }
  });
};
